import Toggle from 'components/new/Toggle';
import Header from '../Header';
import { AccountType } from 'further-types/investor';
import { Column, Row } from 'further-ui/components/Layout';
import FormControlLabel from 'further-ui/components/FormControlLabel';
import {
  Box,
  Button,
  Checkbox,
  TextField,
  styled,
  useTheme,
} from '@mui/material';
import { onboardingAdmin } from 'further-ui/validations';
import { Controller, useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import FormField from 'further-ui/components/FormField';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import useSearchInvestors from '../useSearchInvestors';
import api from 'lib/trpcClient';
import { createViewVehicleApplicationRoute } from 'adminConstants/routes';
import { TabType } from '../../ViewInvestor/types';

const Message = styled(Box)(({ theme }) => ({
  backgroundColor: '#E3F2FD', //TODO: use "blue" palette when delivered by Elias
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
  fontSize: theme.fontSizes.regular,
  lineHeight: '150%',
}));

type FormValues = z.infer<typeof onboardingAdmin.AddNewInvestorApplication>;

type Props = {
  vehicleId: string;
  vehicleName: string;
};

const accountTypes = [
  {
    value: AccountType.Individual,
    label: 'Individual',
  },
  {
    value: AccountType.LegalEntity,
    label: 'Legal entity',
  },
];

const NewInvestorForm: React.FC<Props> = ({ vehicleId, vehicleName }) => {
  const history = useHistory();
  const theme = useTheme();
  const { control, watch, handleSubmit, setValue } = useForm<FormValues>({
    resolver: zodResolver(onboardingAdmin.AddNewInvestorApplication),
    defaultValues: {
      accountType: AccountType.Individual,
      addWithCommitment: false,
    },
  });

  const accountType = watch('accountType');
  const fullName =
    accountType === AccountType.Individual
      ? `${watch('firstName')} ${watch('lastName')}`
      : watch('registeredName');

  const investorName = useDebounce(fullName, 300);
  const { investors, isFetching: isSearchingForInvestors } = useSearchInvestors(
    investorName,
    vehicleId,
    true,
  );

  const investorExists = !!investors.length && !isSearchingForInvestors;

  const { mutate: addInvestor, isPending: isAddingInvestor } =
    api.onboarding.addNewInvestor.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Investor added successfully.',
      }),
    );

  const handleAddInvestor = (values: FormValues) => {
    addInvestor(
      {
        ...values,
        vehicleId,
      },
      {
        onSuccess: (vehicleApplicationId) => {
          history.push(
            createViewVehicleApplicationRoute(
              vehicleApplicationId,
              values.addWithCommitment
                ? TabType.CommitmentDetails
                : TabType.InvestorDetails,
            ),
          );
        },
      },
    );
  };

  return (
    <>
      <Row fullWidth justify="center">
        <Toggle
          value={accountType}
          options={accountTypes}
          onChange={(value) => {
            setValue('accountType', value);
          }}
        />
      </Row>
      <Header
        vehicleName={vehicleName}
        isLegalEntity={accountType === AccountType.LegalEntity}
      />
      <Column fullWidth spacing="sm">
        <Column fullWidth spacing="lg">
          {accountType === AccountType.Individual ? (
            <>
              <FormField label="First name">
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="First name"
                      {...field}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FormField>
              <FormField label="Middle name">
                <Controller
                  name="middleName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Middle name (optional)"
                      {...field}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FormField>
              <FormField label="Last name">
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Last name"
                      {...field}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </FormField>
            </>
          ) : (
            <FormField label="Registered name">
              <Controller
                name="registeredName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Registered name"
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
          )}
        </Column>
        <Column fullWidth spacing="sm">
          {investorExists ? (
            <Message>
              An investor with this name already exists in your system. Click{' '}
              <i>Add investor to proceed</i>, or click <i>Back</i> to search.
            </Message>
          ) : null}
          <Controller
            name="addWithCommitment"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <FormControlLabel
                labelPlacement="start"
                justifyContent="center"
                control={
                  <Checkbox
                    size="small"
                    name={name}
                    checked={value}
                    onChange={(_, checked) => {
                      onChange(checked);
                    }}
                  />
                }
                slotProps={{
                  typography: {
                    variant: 'body2',
                    color: 'grey.900',
                    sx: { marginRight: theme.spacing(theme.spacingSizes.xs) },
                  },
                }}
                label="This investor has already completed their subscription"
              />
            )}
          />
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleSubmit(handleAddInvestor)}
            disabled={isAddingInvestor}
          >
            Add investor
          </Button>
        </Column>
      </Column>
    </>
  );
};

export default NewInvestorForm;
